import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, CardActionArea,
  Typography, CircularProgress, Avatar,
} from '@mui/material';
import {
  People as PeopleIcon, Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon, HourglassEmpty as PendingIcon,
  ArrowForward as ArrowForwardIcon, Gavel as GavelIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { adminListarServidores, adminListarDocumentos } from '../../services/adminApi';

const AdminHomePage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const adminUser = (() => {
    try { return JSON.parse(localStorage.getItem('admin_user') || '{}'); }
    catch { return {}; }
  })();

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const [resRegulares, resOficio, resDocs] = await Promise.all([
        adminListarServidores({ page: 1, limit: 1, search: '', oficio: false }),
        adminListarServidores({ page: 1, limit: 1, search: '', oficio: true }),
        adminListarDocumentos({ page: 1, limit: 1 }),
      ]);

      const totalRegulares = Array.isArray(resRegulares)
        ? resRegulares.length
        : resRegulares.total ?? 0;

      const totalOficio = Array.isArray(resOficio)
        ? resOficio.length
        : resOficio.total ?? 0;

      const totaisDoc = Array.isArray(resDocs)
        ? {
            todos: resDocs.length,
            assinados: resDocs.filter((d: any) => d.status === 'ASSINADO').length,
            pendentes: resDocs.filter((d: any) => d.status === 'PENDENTE').length,
          }
        : resDocs.totais ?? {
            todos: resDocs.total ?? 0,
            assinados: 0,
            pendentes: 0,
          };

      setStats({
        total_servidores_regulares: totalRegulares,
        total_servidores_oficio: totalOficio,
        total_documentos: totaisDoc.todos,
        documentos_assinados: totaisDoc.assinados,
        documentos_pendentes: totaisDoc.pendentes,
      });
    } catch {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      label: 'Servidores Regulares',
      value: stats?.total_servidores_regulares ?? '—',
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
      color: '#1565C0', bg: '#E3F2FD',
      route: '/admin/servidores',
      description: 'Cadastros regulares no sistema',
    },
    {
      label: 'Servidores do Ofício',
      value: stats?.total_servidores_oficio ?? '—',
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
      color: '#7B1FA2', bg: '#F3E5F5',
      route: '/admin/servidores/oficio',
      description: 'Registros importados do Ofício da Seduc',
    },
    {
      label: 'Total de Documentos',
      value: stats?.total_documentos ?? '—',
      icon: <DescriptionIcon sx={{ fontSize: 32 }} />,
      color: '#6A1B9A', bg: '#F3E5F5',
      route: '/admin/documentos',
      description: 'Todos os documentos cadastrados',
    },
    {
      label: 'Documentos Assinados',
      value: stats?.documentos_assinados ?? '—',
      icon: <CheckCircleIcon sx={{ fontSize: 32 }} />,
      color: '#2E7D32', bg: '#E8F5E9',
      route: '/admin/documentos?filtro=assinados',
      description: 'Documentos já assinados',
    },
    {
      label: 'Aguardando Assinatura',
      value: stats?.documentos_pendentes ?? '—',
      icon: <PendingIcon sx={{ fontSize: 32 }} />,
      color: '#E65100', bg: '#FFF3E0',
      route: '/admin/documentos?filtro=pendentes',
      description: 'Documentos pendentes',
    },
  ];

  const quickActions = [
    {
      label: 'Servidores Regulares',
      desc: 'Visualize, edite e gerencie os servidores regulares.',
      icon: <PeopleIcon sx={{ fontSize: 28 }} />,
      color: '#1565C0',
      route: '/admin/servidores',
    },
    {
      label: 'Servidores do Ofício',
      desc: 'Consulte os servidores importados da planilha do Ofício da Seduc.',
      icon: <PeopleIcon sx={{ fontSize: 28 }} />,
      color: '#7B1FA2',
      route: '/admin/servidores/oficio',
    },
    {
      label: 'Contratos',
      desc: 'Visualize e gerencie os contratos gerados para os servidores.',
      icon: <GavelIcon sx={{ fontSize: 28 }} />,
      color: '#6A1B9A',
      route: '/admin/documentos?tipo=CONTRATO',
    },
    {
      label: 'Solicitações',
      desc: 'Gerencie os documentos de solicitação enviados aos servidores.',
      icon: <AssignmentIcon sx={{ fontSize: 28 }} />,
      color: '#2E7D32',
      route: '/admin/documentos?tipo=SOLICITACAO',
    },
  ];

  return (
    <Box>
      {/* Welcome */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} color="primary.dark">
          Olá, {adminUser.nome || adminUser.username || 'Administrador'} 👋
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          Painel de controle do sistema SINTET — visão geral em tempo real.
        </Typography>
      </Box>

      {/* Stat Cards */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.label}>
              <Card
                onClick={() => card.route && navigate(card.route)}
                sx={{
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 1 }}>
                        {card.label}
                      </Typography>
                      <Typography variant="h3" fontWeight={800} sx={{ color: card.color, lineHeight: 1 }}>
                        {card.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {card.description}
                      </Typography>
                    </Box>
                    <Avatar sx={{ width: 52, height: 52, bgcolor: card.bg, color: card.color, borderRadius: 2 }}>
                      {card.icon}
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Quick Actions */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Acesso Rápido</Typography>
      <Grid container spacing={3}>
        {quickActions.map((action) => (
          <Grid item xs={12} md={4} key={action.label}>
            <Card sx={{
              borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden',
              '&:hover .arrow-icon': { transform: 'translateX(4px)' },
            }}>
              <CardActionArea onClick={() => navigate(action.route)} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Avatar sx={{ bgcolor: `${action.color}18`, color: action.color, width: 46, height: 46, borderRadius: 2 }}>
                    {action.icon}
                  </Avatar>
                  <Typography variant="h6" fontWeight={700}>{action.label}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{action.desc}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', color: action.color }}>
                  <Typography variant="body2" fontWeight={600}>Acessar</Typography>
                  <ArrowForwardIcon className="arrow-icon"
                    sx={{ fontSize: 16, ml: 0.5, transition: 'transform 0.2s' }} />
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AdminHomePage;